import axiosInstance from '../axios/axios-instance';
import { ActiveQuestDto } from 'shared/interfaces/quest/active-quest.dto';
import { ClaimQuestChestResponseDto } from 'shared/interfaces/quest/claim-quest-chest-response.dto';
import { ClaimQuestResponseDto } from 'shared/interfaces/quest/claim-quest-response.dto';
import { QuestCandidateSetDto, QuestCandidatesResponseDto } from 'shared/interfaces/quest/quest-candidate-set.dto';
import { QuestChestProgressDto } from 'shared/interfaces/quest/quest-chest-progress.dto';
import { SelectQuestCandidateResponseDto } from 'shared/interfaces/quest/select-quest-candidate-response.dto';

interface QuestApi {
  getActiveQuests: () => Promise<ActiveQuestDto[]>;
  getQuestChestProgress: () => Promise<QuestChestProgressDto>;
  getCandidates: () => Promise<QuestCandidatesResponseDto>;
  selectCandidate: (candidateId: number) => Promise<SelectQuestCandidateResponseDto>;
  rerollCandidates: (candidateSetId: number) => Promise<QuestCandidateSetDto>;
  markQuestNotificationsAsViewed: () => Promise<void>;
  claimQuest: (questId: number) => Promise<ClaimQuestResponseDto>;
  deleteQuest: (questId: number) => Promise<void>;
  claimQuestChest: () => Promise<ClaimQuestChestResponseDto>;
}

export const questApi: QuestApi = {
  /**
   * Get the active quests for the current user.
   * @returns The active quests.
   */
  async getActiveQuests(): Promise<ActiveQuestDto[]> {
    const response = await axiosInstance.get<ActiveQuestDto[]>('/quest/active');
    return response.data;
  },

  /**
   * Get the quest progress for the current user (achieved days, can claim chest, chest claimed on).
   * @returns The quest progress.
   */
  async getQuestChestProgress(): Promise<QuestChestProgressDto> {
    const response = await axiosInstance.get<QuestChestProgressDto>('/quest/quest-chest-progress');
    return response.data;
  },

  async getCandidates(): Promise<QuestCandidatesResponseDto> {
    const response = await axiosInstance.get<QuestCandidatesResponseDto>('/quest/candidates');
    return response.data;
  },

  async selectCandidate(candidateId: number): Promise<SelectQuestCandidateResponseDto> {
    const response = await axiosInstance.post<SelectQuestCandidateResponseDto>(
      `/quest/candidates/${candidateId}/select`
    );
    return response.data;
  },

  async rerollCandidates(candidateSetId: number): Promise<QuestCandidateSetDto> {
    const response = await axiosInstance.post<QuestCandidateSetDto>('/quest/candidates/reroll', {
      candidateSetId,
    });
    return response.data;
  },

  /**
   * Mark the quest notifications as viewed (removes red notification dot from the quest icon in nav bar).
   * @returns The void.
   */
  async markQuestNotificationsAsViewed(): Promise<void> {
    await axiosInstance.post('/quest/reset-notify-complete');
  },

  /**
   * Claim a quest reward and payout the XP/points to user.
   * @param questId - The ID of the quest.
   * @returns The reward progress and level progress.
   */
  async claimQuest(questId: number): Promise<ClaimQuestResponseDto> {
    const response = await axiosInstance.post<ClaimQuestResponseDto>(`/quest/${questId}/claim`);
    return response.data;
  },

  /**
   * Delete a quest by ID and user ID.
   * @param questId - The ID of the quest.
   * @returns The void.
   */
  async deleteQuest(questId: number): Promise<void> {
    await axiosInstance.delete(`/quest/${questId}`);
  },

  /**
   * Claim a quest chest reward and get the reward progress, level progress, and streak freeze progress.
   * @returns The reward progress, level progress, and streak freeze progress.
   */
  async claimQuestChest(): Promise<ClaimQuestChestResponseDto> {
    const response = await axiosInstance.post<ClaimQuestChestResponseDto>('/quest-chest/claim');
    return response.data;
  },
};
